import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface

interface Props {
  Menuprops: menuInfo;
}

/**
 * 배부율 컬럼의 field 접두사. `qt_` + COS005 코드 로 만든다. (예: 배부기준 001 → qt_001)
 *
 * pms_pjt_distrate 는 프로젝트 × 배부기준 1행(세로)인데 화면은 프로젝트 1행 × 배부기준 n열(가로)이라
 * 조회할 때 피벗하고 저장할 때 되돌린다. 배부율 컬럼은 sys_formgridcolumn 에 두지 않고
 * COS005 를 읽어 런타임에 만든다 — 코드가 늘면 컬럼도 같이 늘어난다.
 * (품목별배부율등록 SN_COS_MAS_REG_003 과 같은 방식)
 */
const RATE_PREFIX = "qt_";

/** 상단 버튼(sys_headerctrl)이 달리는 cd_form. getInitForm 이 이 키로 내려준다. */
const HEADER_FORM = "grid1";

/**
 * 공통프로젝트. 공통프로젝트로 모인 금액을 각 프로젝트에 배부하는 화면이므로
 * 공통프로젝트 자신은 배부 대상이 될 수 없다. 전개에서도 빼고 직접 입력도 막는다.
 */
const TP_PJT_COMMON = "9";

const SN_PMS_BAS_REG_008 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRef = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const { userInfo } = global();
  /** 프로젝트전개·이전자료참조로 채운 내용은 셀 편집이 아니라 그리드 변경추적에 안 잡힌다. */
  const pendingRef = useRef(false);
  /** 원가기간 도움창 선택 → 자동조회. 시작/종료가 연달아 바뀌므로 마지막 것만 실행한다. */
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColums1] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  /** COS005 사용유무 'Y' — 배부율 컬럼의 원천이자 저장 시 펼칠 대상 */
  const [distCodes, setDistCodes] = useState<any[]>([]);
  /** 상단 버튼 메타. getInitForm 이 이미 {controls, data} 로 가공해 주므로 다시 감싸지 않는다. */
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });
  //#endregion

  //#region  공통 유틸
  /** 'YYYYMM' 의 직전월 */
  const prevMonth = (ym: string) => {
    const text = String(ym ?? "");
    if (text.length < 6) return text;
    const date = new Date(
      Number(text.slice(0, 4)),
      Number(text.slice(4, 6)) - 2,
      1
    );
    return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
  };

  /** 'YYYYMM' → '2026/02'. 안내문구용 — 조회조건 표기와 같은 형식으로 맞춘다. */
  const formatYm = (ym: string) => {
    const text = String(ym ?? "");
    return text.length === 6 ? `${text.slice(0, 4)}/${text.slice(4, 6)}` : text;
  };

  /** 조회조건의 원가기간 종료월. 저장되는 행의 ym_end 가 된다. */
  const getYmEnd = () =>
    String(searchRef.current?.getFormdata()?.dt_to ?? "").substring(0, 6);

  /** 조회조건의 원가기간 시작월. ym_start 컬럼에 담는다. */
  const getYmStart = () =>
    String(searchRef.current?.getFormdata()?.dt_from ?? "").substring(0, 6);

  /** 엑셀에서 붙여넣으면 '1,000' 처럼 들어올 수 있다. 비었으면 null. */
  const toNumber = (value: any) => {
    if (value === null || value === undefined) return null;
    const text = String(value).replace(/,/g, "").trim();
    if (text === "") return null;
    const num = Number(text);
    return Number.isFinite(num) ? num : null;
  };

  /** 그리드에 실제로 떠 있는 행 (rowData state 는 셀 편집을 반영하지 않는다) */
  const collectRows = () => {
    const rows: any[] = [];
    gridApiRef.current?.forEachNode((node: any) => {
      if (node.data) rows.push(node.data);
    });
    return rows;
  };

  /** 공통프로젝트 여부. 조회·전개 쿼리에서도 거르지만 직접 입력 경로가 남아 여기서도 본다. */
  const isCommonPjt = (row: any) =>
    String(row?.tp_pjt ?? "") === TP_PJT_COMMON;

  /** 세로 조회결과 → 프로젝트 1행으로 피벗 */
  const pivotByPjt = (rows: any[], ymEnd?: string) => {
    const map = new Map<string, any>();
    (rows ?? []).forEach((row: any) => {
      const key = String(row.cd_pjt ?? "");
      if (key === "") return;

      let merged = map.get(key);
      if (!merged) {
        merged = {
          cd_firm: row.cd_firm ?? userInfo.cd_firm,
          cd_biz: row.cd_biz,
          ym_end: ymEnd ?? row.ym_end,
          cd_pjt: row.cd_pjt,
          nm_pjt: row.nm_pjt,
          // fg_pjt·tp_pjt 는 화면에 안 보이지만 공통프로젝트 판정에 쓰인다.
          fg_pjt: row.fg_pjt,
          tp_pjt: row.tp_pjt,
        };
        map.set(key, merged);
      }

      // 배부율이 하나도 없는 프로젝트는 fg_dist 가 null 로 온다 (프로젝트전개의 left join)
      const flag = row.fg_dist;
      if (flag !== null && flag !== undefined && flag !== "") {
        merged[RATE_PREFIX + flag] = row.qt_rate;
      }
    });
    return [...map.values()];
  };
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      try {
        const rtn: any = await getInitFormData();

        // COS005 사용유무 'Y' — 이 목록이 곧 환산율 컬럼이다.
        // 대상 코드를 바꾸려면 프론트가 아니라 sn_pms_bas_reg_008_04 를 고친다.
        const codeData: any = await fnGetDataSet({
          id: "sn_pms_bas_reg_008_04",
          params: {},
        });
        const codes: any[] = codeData?.[0] ?? [];

        const baseColumn = rtn.grid?.grid1?.[0] ?? {};
        const nextSeq = (rtn.grid?.grid1?.length ?? 0) + 1;
        const rateColumns = codes.map((code: any, index: number) => ({
          ...baseColumn,
          cd_column: RATE_PREFIX + code.cd_flag,
          nm_column: code.nm_flag,
          tp_column: "SnNumberBox",
          fg_visible: "Y",
          yn_required: "N",
          yn_sum: "Y", // 요청서 하단 sum 행
          yn_allowsorting: "Y",
          yn_fixed: "N",
          yn_drilldown: "N",
          yn_system: "N",
          // 제목이 COS005 명칭이라 길이가 제각각이다. 글자수에 맞춰 잡고 최소 110 은 준다.
          // (정렬 화살표·메뉴 아이콘 자리로 40 을 더한다)
          qt_width: Math.max(110, String(code.nm_flag ?? "").length * 15 + 40),
          qt_groupindex: -1,
          no_seq: nextSeq + index,
          dc_editcon: "",
          cd_popup: "",
          cd_clas: "",
          cd_displaycolumn: "",
          dc_sql: "",
          dc_filter: null,
          tp_format: "#,##0.0000", // 배부율 — 천단위 + 소수 4자리(fnFormatNumberByPattern 패턴)
          nm_group: null,
          cd_value: null,
        }));

        setDistCodes(codes);
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums1([...(rtn.grid?.grid1 ?? []), ...rateColumns]);
        setHeaderInfo(rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  /**
   * 저장하지 않은 변경이 있으면 먼저 묻는다.
   * 저장까지 끝났으면 true — 저장이 재조회를 이미 했으므로 호출부가 또 조회하지 않는다.
   */
  const confirmDiscard = async () => {
    const changed =
      pendingRef.current ||
      (gridApiRef.current?.getChange()?.modifiedRowcount ?? 0) > 0;
    if (!changed) return false;

    const confirm = await showDialog(
      "confirm",
      "수정된 내용이 있습니다. 저장하시겠습니까?"
    );
    if (!confirm) return false;
    return !!(await onSave());
  };

  /**
   * 실제 조회. 저장 직후 재조회에서도 쓰므로 여기서 변경 확인을 하지 않는다.
   * (저장 시점엔 resetUpdate 전이라 변경분이 남아 있어, 확인을 넣으면 저장 직후
   *  "수정된 내용이 있습니다" 가 한 번 더 뜬다.)
   */
  const loadGrid = async () => {
    try {
      const formdata = searchRef.current.getFormdata();
      setDataSource1([]);

      const data: any = await fnGetDataSet({
        id: "sn_pms_bas_reg_008_01",
        params: { ...formdata, ym_end: getYmEnd() },
      });
      const rows = pivotByPjt(data?.[0] ?? []);
      setDataSource1(rows);

      if (rows.length > 0) {
        // 그리드 바인딩을 기다린 뒤 첫 행 포커스
        await new Promise((resolve) => setTimeout(resolve, 50));
        gridApiRef.current.selectAndFocusRow(0);
      }

      pendingRef.current = false;
      gridApiRef.current.resetUpdate();
    } catch (error: any) {}
  };

  /** 툴바 조회 버튼. 저장하지 않은 변경이 있으면 먼저 묻는다. */
  const onSearch = async () => {
    const savedAndReloaded = await confirmDiscard();
    if (savedAndReloaded) return; // 저장 과정에서 이미 조회했다
    await loadGrid();
  };

  /**
   * 원가기간 도움창에서 기간을 고르면 바로 조회한다.
   * source 가 'popup' 일 때만 반응한다 — 직접 입력 중에는 조회하지 않는다.
   * (조회조건만 바뀌고 그리드에 이전 기간 자료가 남아 있으면 오해를 부른다)
   */
  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  const onInsert = async () => {
    try {
      gridApiRef.current?.addRow({});
    } catch (error: any) {}
  };

  const onDelete = async () => {
    gridApiRef.current?.deleteSelectedRows();
  };

  /** GridV2 는 인자 없이 호출하고 기본값 객체를 돌려받는다. */
  const onBeforeAddRow = () => {
    try {
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: searchRef.current.getFormdata().cd_biz,
        ym_end: getYmEnd(),
      };
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  /** 상단 버튼(sys_headerctrl)의 cd_param 이 sender 로 넘어온다. */
  const onBtnEtcClick = async (sender: string) => {
    const formdata = searchRef.current.getFormdata();
    const ymEnd = getYmEnd();

    if (sender === "deploy") {
      // 프로젝트전개 — 조회조건에 맞는 프로젝트를 하단에 뿌린다. 이미 등록된 배부율은 살린다.
      // 공통프로젝트(tp_pjt='9')는 쿼리에서 제외된다.
      await confirmDiscard();
      const confirm = await showDialog(
        "confirm",
        "해당월로 프로젝트전개 하시겠습니까"
      );
      if (!confirm) return;

      const data: any = await fnGetDataSet({
        id: "sn_pms_bas_reg_008_02",
        params: { ...formdata, ym_end: ymEnd },
      });
      const rows = pivotByPjt(data?.[0] ?? [], ymEnd);
      if (rows.length === 0) {
        await showDialog("warning", "데이터가 없습니다.");
        return;
      }

      setDataSource1(rows);
      gridApiRef.current?.resetUpdate();
      pendingRef.current = true;
      // 화면에 뿌리기만 한 상태다. 저장 전이라는 걸 같이 알린다.
      await showDialog(
        "alter",
        `프로젝트전개가 완료되었습니다. (${rows.length}건)\n저장해야 반영됩니다.`
      );
      return;
    }

    if (sender === "prev") {
      // 이전자료참조 — 원가기간 시작월의 직전월 배부율을 이번 달 자료로 가져온다.
      // 조회(_01)와 조건이 같고 대상 월만 다르므로 같은 쿼리를 쓴다.
      await confirmDiscard();

      const refYm = prevMonth(String(formdata.dt_from ?? "").substring(0, 6));
      const confirmPrev = await showDialog(
        "confirm",
        `${formatYm(refYm)} 자료를 가져오시겠습니까?`
      );
      if (!confirmPrev) return;

      const data: any = await fnGetDataSet({
        id: "sn_pms_bas_reg_008_01",
        params: { ...formdata, ym_end: refYm },
      });

      // 가져온 행은 직전월이 아니라 이번 달(ym_end) 자료로 저장되어야 한다
      const rows = pivotByPjt(data?.[0] ?? [], ymEnd);
      if (rows.length === 0) {
        await showDialog("warning", "데이터가 없습니다.");
        return;
      }

      setDataSource1(rows);
      gridApiRef.current?.resetUpdate();
      pendingRef.current = true;
      await showDialog(
        "alter",
        `${formatYm(refYm)} 자료를 가져왔습니다. (${rows.length}건)\n저장해야 반영됩니다.`
      );
    }
  };

  const onSave = async () => {
    try {
      gridApiRef.current?.stopEditing();
      if (!gridApiRef.current.requiredValuesCheck()) return false;

      const changes = gridApiRef.current.getChange();
      if (!pendingRef.current && changes.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const ymEnd = getYmEnd();
      const ymStart = getYmStart();
      const cdBiz = searchRef.current.getFormdata().cd_biz;
      const keyOf = (row: any, flag: string) => ({
        cd_firm: userInfo.cd_firm,
        cd_biz: row.cd_biz || cdBiz,
        ym_end: row.ym_end || ymEnd,
        cd_pjt: row.cd_pjt,
        fg_dist: flag,
      });

      const rows = collectRows();
      if (rows.some((row) => !String(row.cd_pjt ?? "").trim())) {
        await showDialog("alter", "프로젝트는 필수값입니다.");
        return false;
      }

      // 공통프로젝트는 배부 대상이 아니다. 전개·조회에서는 이미 빠지지만
      // 행추가로 직접 입력하는 경로가 남아 있어 저장 직전에 한 번 더 막는다.
      const common = rows.find(isCommonPjt);
      if (common) {
        await showDialog(
          "alter",
          `공통프로젝트는 배부대상이 될 수 없습니다. (${common.cd_pjt})`
        );
        return false;
      }

      // 프로젝트 단위로 전부 지우고 값이 있는 배부기준만 다시 넣는다.
      // 없는 행을 지우는 것은 Spring Data delete() 가 조용히 넘어가므로 안전하다.
      // ponytail: 화면에 보이는 행 전체를 다시 쓴다. 수천 건으로 늘면 변경행만 추리도록 바꿀 것.
      const deleteList: any[] = [];
      const insertList: any[] = [];

      rows.forEach((row) => {
        distCodes.forEach((code: any) => {
          deleteList.push(keyOf(row, code.cd_flag));
          const rate = toNumber(row[RATE_PREFIX + code.cd_flag]);
          if (rate !== null) {
            insertList.push({
              ...keyOf(row, code.cd_flag),
              qt_rate: rate,
              ym_start: ymStart,
            });
          }
        });
      });

      // 삭제된 행은 화면에서 사라져 위 루프에 없다. 따로 지운다.
      (changes.delete ?? []).forEach((row: any) => {
        if (!row.cd_pjt) return;
        distCodes.forEach((code: any) =>
          deleteList.push(keyOf(row, code.cd_flag))
        );
      });

      if (deleteList.length === 0 && insertList.length === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const SaveData = {
        pms_pjt_distrate: {
          insert: insertList,
          update: [],
          delete: deleteList,
          modifiedRowcount: insertList.length + deleteList.length,
        },
      };

      const ok = await fnSave("SN_PMS_BAS_REG_008_SAVE_01", SaveData);
      if (ok) {
        pendingRef.current = false;
        // confirmDiscard 를 거치지 않는 loadGrid 를 쓴다. onSearch 를 부르면
        // 아직 남아 있는 변경추적 때문에 저장 직후 저장 여부를 또 묻게 된다.
        await loadGrid();
      }
      return ok;
    } catch (error: any) {
      return false;
    }
  };
  //#endregion

  /** 프로젝트를 입력하면 프로젝트명 등을 채운다. 공통프로젝트면 되돌린다. */
  const onCellValueChanged = async (event: any) => {
    if (!event) return;
    if (event.colId !== "cd_pjt") return;

    const node = event.rowNode;
    const cdPjt = String(event.rowData?.cd_pjt ?? "").trim();
    const fields = ["nm_pjt", "fg_pjt", "tp_pjt"];

    const clear = () =>
      fields.forEach((field) =>
        gridApiRef.current.setValueImmediately(node, field, "")
      );

    if (cdPjt === "") {
      clear();
      return;
    }

    const rtn: any = await fnGetDataSet({
      id: "sn_pms_bas_reg_008_05",
      params: { cd_firm: userInfo.cd_firm, cd_pjt: cdPjt },
    });
    const data = Array.isArray(rtn?.[0]) ? rtn[0] : [];
    if (data.length === 0) return;

    // 공통프로젝트는 배부 대상이 아니다. 입력 자체를 되돌린다.
    if (String(data[0].tp_pjt ?? "") === TP_PJT_COMMON) {
      await showDialog(
        "alter",
        "공통프로젝트는 배부대상이 될 수 없습니다."
      );
      gridApiRef.current.setValueImmediately(node, "cd_pjt", "");
      clear();
      return;
    }

    fields.forEach((field) =>
      gridApiRef.current.setValueImmediately(node, field, data[0][field])
    );
  };

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1">
        <SnGrid
          ref={gridApiRef} // Grid Name
          columnConfig={columns1} // 컬럼 설정 전달
          rowData={dataSource1} // Datasource
          etcProps={{
            isEditable: true, // 수정 가능여부
            rowSelection: "single", // 로우 선택 모드 (단일)
            title: "프로젝트별배부율",
          }}
          events={{
            onBeforeAddRow,
            onCellValueChanged,
            onBtnEtcClick, // 프로젝트전개 / 이전자료참조 (sys_headerctrl)
          }}
          headerInfo={headerInfo}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_PMS_BAS_REG_008;
